import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, ErrorState, PageHeader } from "../components/ui";
import { logoutSession } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { loadOnboardingDraft } from "../lib/onboarding";
import { useShellSession } from "../hooks/useShellSession";

function readable(value: string | boolean | null | undefined): string {
  if (value === null || value === undefined || value === "") {
    return "Set in iOS app";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  return value.replace(/_/g, " ");
}

export function SettingsPage() {
  const { session } = useShellSession();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const onboardingDraft = useMemo(() => loadOnboardingDraft(), []);

  async function handleLogout(): Promise<void> {
    setLoggingOut(true);
    trackEvent("sign_out", { surface: "settings" });
    try {
      const result = await logoutSession();
      window.location.href = result.redirectUrl || "/";
    } catch (err) {
      setLoggingOut(false);
      setError(err instanceof Error ? err.message : "Unable to sign out.");
    }
  }

  return (
    <div data-testid="settings-page" className="space-y-5 pb-4">
      <PageHeader title="Settings" subtitle="Account and read-only profile." />

      <section className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Account</p>
        <Card>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-base font-semibold">{session.user?.name || "PantryRx Member"}</p>
              <p className="mt-1 text-sm text-[var(--pr-text-muted)]">{session.user?.email}</p>
              <p className="mt-2 text-xs uppercase tracking-wide text-[var(--pr-text-muted)]">
                Role: {session.user?.role || "member"}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={() => navigate("/app/more")}>
                Back to more
              </Button>
              <Button
                variant="ghost"
                className="border border-[var(--pr-border)] text-rose-600"
                disabled={loggingOut}
                onClick={() => void handleLogout()}
              >
                {loggingOut ? "Signing out..." : "Sign out"}
              </Button>
            </div>
          </div>
        </Card>
      </section>

      <section className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Onboarding profile</p>
        <Card>
          <p className="text-sm text-[var(--pr-text-muted)]">
            Read-only on web. Update onboarding details from the iOS PantryRx app.
          </p>

          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            <div className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Gender</p>
              <p className="text-sm font-semibold capitalize">{readable(onboardingDraft.gender)}</p>
            </div>
            <div className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Age range</p>
              <p className="text-sm font-semibold capitalize">{readable(onboardingDraft.age_range)}</p>
            </div>
            <div className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Diet</p>
              <p className="text-sm font-semibold capitalize">{readable(onboardingDraft.diet)}</p>
            </div>
            <div className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">
                Works with dietitian
              </p>
              <p className="text-sm font-semibold">{readable(onboardingDraft.works_with_dietitian)}</p>
            </div>
            <div className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2 sm:col-span-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Primary goal</p>
              <p className="text-sm font-semibold capitalize">{readable(onboardingDraft.goal)}</p>
            </div>
          </div>
        </Card>
      </section>

      <section className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Credits</p>
        <Card>
          <p className="text-sm text-[var(--pr-text-muted)]">
            Credit purchases are available on iOS only. If you run out, open PantryRx on iPhone to add credits.
          </p>
        </Card>
      </section>

      {error ? <ErrorState message={error} /> : null}
    </div>
  );
}
